import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { Link, Navigate, useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import Atendimento from "../../../components/Atendimento";
import CustomerAwareness from "../../../components/CustomerAwareness";
import Produtos from "../../../components/Produtos";
import VendaProdutos from "../../../components/VendaProdutos";
import VendaPassagens from "../../../components/VendaPassagens";
import SurveyAnalysis from "../../../components/SurveyAnalysis";
import { WorkspaceSegments } from "../../../constants/workspaceSegments";
import { exportPlatformDashboardCsv, listPlatformDominios, listPlatformSurveys } from "../../../api/client";
import { readDashboardDate, writeDashboardDate } from "../../../utils/workspaceStorage";
import { applyDayjsLocale, datePickerLocalizationForI18n } from "../../../utils/dayjsLocale";
import {
  getInitialDashboardDateRange,
  getDashboardInclusiveSpanDays,
  normalizeDashboardDateRange,
} from "../../../utils/dashboardDateRange";
import { applyDashboardDatesFromSearchParams, applyDashboardWorkspacesFromSearchParams } from "../../../utils/dashboardDeepLink";
import { PlatformDashboardProvider } from "./PlatformDashboardContext";
import { DashboardJobBootstrapContext } from "../../../context/DashboardJobBootstrapContext";
import { DashboardJobsProvider } from "../../../context/DashboardJobsContext";
import DashboardBackgroundJobsPanel from "../../../components/dashboard/DashboardBackgroundJobsPanel";
import DashboardPageToolbar from "../../../components/dashboard/DashboardPageToolbar";
import DashboardPresentationShareDialog from "../../../components/dashboard/DashboardPresentationShareDialog";
import { DashboardShareReadinessProvider } from "../../../context/DashboardShareReadinessContext";
import DashboardCachesDialog from "../../../components/dashboard/DashboardCachesDialog";
import { usePlatformDashboardTerminology } from "./hooks/usePlatformDashboardTerminology";
import { platformDashboardTabPath } from "./platformConsoleRoutes";
import PlatformDashboardLiveTab from "./PlatformDashboardLiveTab";

dayjs.extend(localizedFormat);
applyDayjsLocale("pt-BR");

const initialDashboardDates = getInitialDashboardDateRange(readDashboardDate);

function persistDashboardDates(start, end) {
  try {
    if (start?.isValid?.()) writeDashboardDate("start", start.format("YYYY-MM-DD"));
    if (end?.isValid?.()) writeDashboardDate("end", end.format("YYYY-MM-DD"));
  } catch {
    /* ignore */
  }
}

function applyNormalizedRange(setStartDate, setEndDate, start, end, changed, priorSpanDays) {
  const normalized = normalizeDashboardDateRange(start, end, { changed, priorSpanDays });
  setStartDate(normalized.start);
  setEndDate(normalized.end);
  persistDashboardDates(normalized.start, normalized.end);
  return normalized;
}

const dateFieldSx = {
  backgroundColor: "white",
  "& .MuiInputBase-input": { color: "black" },
};

function PlatformDashboardPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t, i18n } = useTranslation("settings");
  const { t: tApp } = useTranslation("app");
  const { tab: tabParam } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Lazy + Suspense no 1º clique podem montar sem outlet por um tick — não destruir a árvore.
  const { registerRefresh, stopRefreshing } = useOutletContext() || {};
  const { entityPlural } = usePlatformDashboardTerminology();

  const [startDate, setStartDate] = useState(initialDashboardDates.start);
  const [endDate, setEndDate] = useState(initialDashboardDates.end);
  const [bootstrapJobId, setBootstrapJobId] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [cachesOpen, setCachesOpen] = useState(false);
  const [workspaceOptions, setWorkspaceOptions] = useState([]);
  const [selectedWorkspaces, setSelectedWorkspaces] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [hasSurveys, setHasSurveys] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const [billingSignal, setBillingSignal] = useState(null);

  const datePickerLocale = useMemo(
    () => datePickerLocalizationForI18n(i18n.language),
    [i18n.language]
  );
  const maxSelectableDate = useMemo(() => dayjs().endOf("day"), []);

  useEffect(() => {
    persistDashboardDates(startDate, endDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    applyDayjsLocale(i18n.language);
  }, [i18n.language]);

  useEffect(() => {
    applyDashboardWorkspacesFromSearchParams(searchParams, workspaceOptions, setSelectedWorkspaces);
  }, [searchParams, workspaceOptions]);

  useEffect(() => {
    const applied = applyDashboardDatesFromSearchParams(searchParams, {
      onApply: (start, end) => {
        if (start) setStartDate(start);
        if (end) setEndDate(end);
        persistDashboardDates(start, end);
      },
    });
    const jobFromUrl = searchParams.get("job");
    if (jobFromUrl) {
      setBootstrapJobId(jobFromUrl);
      const next = new URLSearchParams(searchParams);
      next.delete("job");
      const qs = next.toString();
      navigate({ search: qs ? `?${qs}` : "" }, { replace: true });
    }
    if (applied || jobFromUrl) {
      queryClient.invalidateQueries(["platform-dashboard"]);
      queryClient.invalidateQueries(["platform-dashboard-preview"]);
      queryClient.invalidateQueries(["platform-dashboard-active-jobs"]);
      queryClient.invalidateQueries(["platform-survey-analytics"]);
      queryClient.invalidateQueries(["platform-survey-analytics-preview"]);
    }
  }, [searchParams, queryClient, navigate]);

  useEffect(() => {
    const onLimitHit = (ev) => {
      const msg =
        ev?.detail?.message ||
        t("dashboardPage.billingSignal", {
          defaultValue: "Seu plano atingiu um limite. Avalie upgrade ou adicional.",
        });
      setBillingSignal({ payload: { message: msg } });
    };
    window.addEventListener("xpro:billing-limit-hit", onLimitHit);
    return () => window.removeEventListener("xpro:billing-limit-hit", onLimitHit);
  }, [t]);

  useEffect(() => {
    if (!bootstrapJobId) return undefined;
    const timer = setTimeout(() => setBootstrapJobId(null), 45_000);
    return () => clearTimeout(timer);
  }, [bootstrapJobId]);

  const loadWorkspaces = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await listPlatformDominios({ limit: 500 });
      setWorkspaceOptions(Array.isArray(res?.items) ? res.items : []);
    } catch {
      setWorkspaceOptions([]);
    } finally {
      setLoadingList(false);
      stopRefreshing?.();
    }
  }, [stopRefreshing]);

  useEffect(() => {
    loadWorkspaces();
  }, [loadWorkspaces]);

  const selectedIds = useMemo(
    () => selectedWorkspaces.map((w) => Number(w.id)).filter((x) => Number.isFinite(x) && x > 0),
    [selectedWorkspaces]
  );

  const loadSurveysAvailability = useCallback(async () => {
    try {
      const payload = await listPlatformSurveys({
        page: 1,
        limit: 1,
        dominio_ids: selectedIds,
      });
      const total = Number(payload?.pagination?.total ?? 0);
      setHasSurveys(total > 0);
    } catch {
      setHasSurveys(false);
    }
  }, [selectedIds]);

  useEffect(() => {
    loadSurveysAvailability();
  }, [loadSurveysAvailability, refreshToken]);

  useEffect(() => {
    if (typeof registerRefresh !== "function") return undefined;
    registerRefresh(() => {
      setRefreshToken((x) => x + 1);
      loadWorkspaces();
      loadSurveysAvailability();
    });
    return () => registerRefresh(() => stopRefreshing?.());
  }, [registerRefresh, stopRefreshing, loadWorkspaces, loadSurveysAvailability]);

  const scopeWorkspaces = selectedWorkspaces.length ? selectedWorkspaces : workspaceOptions;
  const hasPassengerTransport = scopeWorkspaces.some(
    (w) => String(w.workspace_segment || "").toLowerCase() === WorkspaceSegments.PassengerTransport
  );
  const hasGenericProducts = scopeWorkspaces.some((w) => {
    const seg = String(w.workspace_segment || "").toLowerCase();
    return !seg || seg === WorkspaceSegments.Generic || seg === WorkspaceSegments.AutoParts;
  });

  const tabsDef = useMemo(() => {
    const start = startDate.format("YYYY-MM-DD");
    const end = endDate.format("YYYY-MM-DD");
    const base = [
      {
        key: "live",
        label: t("dashboardPage.tabLive"),
        section: "live",
        render: () => <PlatformDashboardLiveTab />,
      },
      {
        key: "overview",
        label: t("dashboardPage.tabOverview"),
        section: "atendimento",
        render: () => <Atendimento start={start} end={end} />,
      },
      {
        key: "clients",
        label: t("dashboardPage.tabClients"),
        section: "clientes",
        render: () => <CustomerAwareness start={start} end={end} />,
      },
      {
        key: "products",
        label: t("dashboardPage.tabProducts"),
        section: "produtos",
        segments: [WorkspaceSegments.Generic, WorkspaceSegments.AutoParts],
        render: () => <Produtos start={start} end={end} />,
      },
      {
        key: "sales",
        label: t("dashboardPage.tabSales"),
        section: "vendas",
        render: () => <VendaProdutos start={start} end={end} />,
      },
      {
        key: "busTickets",
        label: t("dashboardPage.tabBusTickets"),
        section: "venda-passagens",
        segments: [WorkspaceSegments.PassengerTransport],
        render: () => <VendaPassagens start={start} end={end} />,
      },
      {
        key: "surveys",
        label: t("dashboardPage.tabSurveys"),
        section: "surveys",
        render: () => <SurveyAnalysis start={start} end={end} />,
      },
    ];
    return base.filter((tab) => {
      if (tab.key === "surveys") return hasSurveys === true;
      if (!Array.isArray(tab.segments) || !tab.segments.length) return true;
      if (tab.segments.includes(WorkspaceSegments.PassengerTransport)) return hasPassengerTransport;
      if (tab.key === "products") return hasGenericProducts;
      return true;
    });
  }, [t, startDate, endDate, hasPassengerTransport, hasGenericProducts, hasSurveys, refreshToken]);

  const sectionByTab = useMemo(() => {
    const m = {};
    tabsDef.forEach((tab, idx) => {
      m[idx] = tab.section;
    });
    return m;
  }, [tabsDef]);

  const activeTab = useMemo(() => {
    if (!tabParam) return 0;
    const idx = tabsDef.findIndex((tab) => tab.key === tabParam);
    return idx >= 0 ? idx : -1;
  }, [tabParam, tabsDef]);

  const activeTabDef = activeTab >= 0 ? tabsDef[activeTab] : null;
  const activeSection = activeTab >= 0 ? tabsDef[activeTab]?.section : null;
  const canSharePresentation = activeSection && activeSection !== "live";
  const platformSurveyKey =
    activeTabDef?.key === "surveys" ? searchParams.get("survey") || "" : "";

  const providerValue = useMemo(
    () => ({
      enabled: activeSection !== "live" && activeSection !== "surveys",
      liveEnabled: activeSection === "live",
      surveysEnabled: activeSection === "surveys",
      dominioIds: selectedIds,
      workspaces: scopeWorkspaces,
    }),
    [activeSection, selectedIds, scopeWorkspaces]
  );

  const handleTabChange = (_event, newValue) => {
    const key = tabsDef[newValue]?.key;
    if (key) navigate(platformDashboardTabPath(key));
  };

  if (tabParam === "surveys" && hasSurveys === null) {
    return (
      <DashboardJobsProvider variant="platform">
        <DashboardJobBootstrapContext.Provider value={bootstrapJobId}>
          <LocalizationProvider
            key={datePickerLocale.adapterLocale}
            dateAdapter={AdapterDayjs}
            adapterLocale={datePickerLocale.adapterLocale}
            localeText={datePickerLocale.localeText}
          >
            <Box sx={{ p: 2 }}>
              <Typography color="text.secondary">{t("dashboardPage.loadingSurveysTab", "Verificando pesquisas…")}</Typography>
            </Box>
          </LocalizationProvider>
        </DashboardJobBootstrapContext.Provider>
      </DashboardJobsProvider>
    );
  }

  if (activeTab < 0) {
    const fallbackKey = tabsDef[0]?.key || "live";
    return <Navigate to={platformDashboardTabPath(fallbackKey)} replace />;
  }

  const scopeLabel =
    selectedWorkspaces.length === 0
      ? tApp("platformPage.dashboard.scopeAll", {
          count: workspaceOptions.length,
          defaultValue: "Todos os workspaces ({{count}})",
        })
      : tApp("platformPage.dashboard.scopeSelected", {
          count: selectedWorkspaces.length,
          defaultValue: "{{count}} selecionados",
        });

  const handleStartDateChange = (newValue) => {
    if (!newValue) return;
    setBootstrapJobId(null);
    const d = dayjs(newValue).startOf("day");
    const priorSpanDays = getDashboardInclusiveSpanDays(startDate, endDate);
    applyNormalizedRange(setStartDate, setEndDate, d, endDate, "start", priorSpanDays);
  };

  const handleEndDateChange = (newValue) => {
    if (!newValue) return;
    setBootstrapJobId(null);
    const d = dayjs(newValue).endOf("day");
    const priorSpanDays = getDashboardInclusiveSpanDays(startDate, endDate);
    applyNormalizedRange(setStartDate, setEndDate, startDate, d, "end", priorSpanDays);
  };

  const handleExport = async () => {
    const section = sectionByTab[activeTab];
    if (!section || section === "surveys" || section === "live") return;
    const params = {
      start: startDate.format("YYYY-MM-DD"),
      end: endDate.format("YYYY-MM-DD"),
      dominio_ids: selectedIds,
    };
    try {
      await exportPlatformDashboardCsv(
        section,
        params,
        `dashboard_${section}_${startDate.format("YYYYMMDD")}_${endDate.format("YYYYMMDD")}.csv`
      );
    } catch (err) {
      console.error("Erro ao exportar:", err);
      const code = err?.code;
      const msg =
        code === "DASHBOARD_EXPORT_STILL_PROCESSING"
          ? t("dashboardPage.exportStillProcessing", {
              defaultValue: "O relatório ainda está sendo gerado. Aguarde a notificação e tente exportar de novo.",
            })
          : code === "DASHBOARD_EXPORT_UNSUPPORTED"
            ? t("dashboardPage.exportUnsupported", {
                defaultValue: "Esta aba não tem exportação de CSV.",
              })
            : err?.userFacing || err?.message || t("dashboardPage.exportFailed");
      alert(msg);
    }
  };

  return (
    <DashboardJobsProvider variant="platform">
      <DashboardJobBootstrapContext.Provider value={bootstrapJobId}>
      <DashboardShareReadinessProvider activeTabKey={activeTabDef?.key}>
    <LocalizationProvider
      key={datePickerLocale.adapterLocale}
      dateAdapter={AdapterDayjs}
      adapterLocale={datePickerLocale.adapterLocale}
      localeText={datePickerLocale.localeText}
    >
      <Box
        className="tour-dashboard-root"
        sx={{
          padding: 1,
          paddingBottom: "150px !important",
          minHeight: "100vh",
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary,
          mx: { xs: -1, sm: -1.5, md: -2 },
          mt: { xs: -1, sm: -1.5 },
        }}
      >
        <DashboardPageToolbar
          title={tApp("platformPage.navDashboard")}
          showPeriod={sectionByTab[activeTab] !== "live"}
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={handleStartDateChange}
          onEndDateChange={handleEndDateChange}
          maxSelectableDate={maxSelectableDate}
          isMobile={isMobile}
          filterSlot={
            sectionByTab[activeTab] !== "live" ? (
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "stretch", sm: "center" }}
                sx={{ width: "100%" }}
              >
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={workspaceOptions}
                  value={selectedWorkspaces}
                  onChange={(_e, value) => setSelectedWorkspaces(value || [])}
                  getOptionLabel={(opt) => String(opt?.dominio || opt?.id || "")}
                  isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                  loading={loadingList}
                  sx={{ flex: 1, minWidth: 0, width: "100%" }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label={tApp("platformPage.dashboard.workspaceFilter", {
                        defaultValue: "Workspaces para visualizar",
                      })}
                      placeholder={tApp("platformPage.dashboard.workspaceFilterPlaceholder", {
                        defaultValue: "Todos — deixe vazio para consolidar tudo",
                      })}
                      sx={{
                        ...dateFieldSx,
                        backgroundColor: "background.paper",
                        width: "100%",
                      }}
                    />
                  )}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={scopeLabel}
                  sx={{
                    alignSelf: { xs: "flex-start", sm: "center" },
                    flexShrink: 0,
                    maxWidth: { xs: "100%", sm: 280 },
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                />
              </Stack>
            ) : null
          }
          showCaches={sectionByTab[activeTab] !== "live"}
          onCachesOpen={() => setCachesOpen(true)}
          showShare={canSharePresentation}
          onShareOpen={() => setShareOpen(true)}
          showExport={sectionByTab[activeTab] !== "live"}
          onExport={handleExport}
          exportDisabled={sectionByTab[activeTab] === "surveys" || sectionByTab[activeTab] === "live"}
        />

        <DashboardBackgroundJobsPanel />

        {billingSignal ? null : null}

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons={isMobile ? "auto" : false}
          sx={{
            borderBottom: 1,
            borderColor: theme.palette.divider,
            "& .MuiTab-root": {
              color: theme.palette.text.primary,
            },
            "& .Mui-selected": {
              color: theme.palette.primary.main,
            },
          }}
        >
          {tabsDef.map((tab, index) => (
            <Tab
              key={tab.key}
              label={tab.label}
              value={index}
              component={Link}
              to={platformDashboardTabPath(tab.key)}
            />
          ))}
        </Tabs>

        <Box className="tour-dashboard-tab-panel" sx={{ marginTop: 2 }}>
          <PlatformDashboardProvider value={providerValue}>
            {tabsDef[activeTab]?.render?.()}
          </PlatformDashboardProvider>
        </Box>
      </Box>
    </LocalizationProvider>
    <DashboardPresentationShareDialog
      open={shareOpen}
      onClose={() => setShareOpen(false)}
      variant="platform"
      tabKey={activeTabDef?.key}
      tabLabel={activeTabDef?.label}
      start={startDate.format("YYYY-MM-DD")}
      end={endDate.format("YYYY-MM-DD")}
      platformSurveyKey={platformSurveyKey || null}
      dominioIds={selectedIds}
    />
    <DashboardCachesDialog
      open={cachesOpen}
      onClose={() => setCachesOpen(false)}
      variant="platform"
    />
      </DashboardShareReadinessProvider>
      </DashboardJobBootstrapContext.Provider>
    </DashboardJobsProvider>
  );
}

export default PlatformDashboardPage;
